import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Badge,
  Button,
  FormField,
  Header,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
} from "@amzn/awsui-components-react";
import {
  KBApprovalDocument,
  useApproveRejectKBDocumentMutation,
} from "pages/gaia-ai/kb-approval-endpoints";
import { useNotifications } from "features/notifications";
import { useFilePreviewSplitPanel } from "components/file-preview-split-panel/hooks";
import { approvalStatusMap } from "./definitions";
import styles from "components/documents-table-os/style.module.scss";
import kbStyles from "./style.module.scss";
import { getFileNameWithExtension } from "components/documents-table-os/utils";

interface KBApprovalDocumentsTableProps {
  kbApprovalDocuments: KBApprovalDocument[];
  reviewId: string;
  teamName: string;
  isPending: boolean;
  isApprover: boolean;
  isDeletionReview?: boolean;
  onActionComplete: () => void;
}

const KBApprovalDocumentsTable: React.FC<KBApprovalDocumentsTableProps> = ({
  kbApprovalDocuments,
  reviewId,
  teamName,
  isPending,
  isApprover,
  isDeletionReview,
  onActionComplete,
}) => {
  const [approveRejectKBDocument] = useApproveRejectKBDocumentMutation();
  const { addNotification } = useNotifications();
  const { setSelectedDocumentId, setSplitPanelOpen, setSource } =
    useFilePreviewSplitPanel();
  const [selectedItems, setSelectedItems] = useState<KBApprovalDocument[]>([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [pendingAction, setPendingAction] = useState<
    "APPROVE" | "REJECT" | null
  >(null);

  const handleBulkAction = async (
    action: "APPROVE" | "REJECT",
    reason?: string,
  ) => {
    setPendingAction(action);
    try {
      const results = await Promise.allSettled(
        selectedItems.map((doc) =>
          approveRejectKBDocument({
            reviewId,
            kbApprovalDocumentId: doc.kbApprovalDocumentId,
            teamName,
            action,
            rejectionReason: reason,
          }).unwrap(),
        ),
      );
      const failCount = results.filter((r) => r.status === "rejected").length;
      if (failCount > 0) {
        addNotification({
          type: "error",
          header: `Failed to ${action.toLowerCase()} ${failCount} document(s)`,
          dismissible: true,
        });
      }
      setSelectedItems([]);
      onActionComplete();
    } finally {
      setPendingAction(null);
    }
  };

  const handleBulkRejectConfirm = async () => {
    await handleBulkAction("REJECT", rejectReason);
    setShowRejectModal(false);
    setRejectReason("");
  };

  const hasRejectedSelected = selectedItems.some(
    (doc) => doc.approvalStatus === "REJECTED",
  );

  return (
    <>
      {!isDeletionReview &&
        kbApprovalDocuments.length > 0 &&
        (() => {
          const source = kbApprovalDocuments[0]?.documentSource;
          if (!source) return null;
          return (
            <Box margin={{ bottom: "s" }}>
              <Badge color={source === "GAIA_OBJECTS" ? "blue" : "grey"}>
                {source === "GAIA_OBJECTS"
                  ? "Source: Existing Gaia documents"
                  : "Source: Uploaded files"}
              </Badge>
            </Box>
          );
        })()}
      <Table
        resizableColumns
        header={
          <Header
            counter={`(${kbApprovalDocuments.length})`}
            actions={
              isPending && isApprover ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    disabled={
                      selectedItems.length === 0 ||
                      hasRejectedSelected ||
                      pendingAction !== null
                    }
                    disabledReason={
                      hasRejectedSelected
                        ? `Cannot approve rejected documents: ${selectedItems
                            .filter((d) => d.approvalStatus === "REJECTED")
                            .map((d) => getFileNameWithExtension(d))
                            .join(", ")}`
                        : undefined
                    }
                    loading={pendingAction === "APPROVE"}
                    onClick={() => handleBulkAction("APPROVE")}
                  >
                    Approve
                  </Button>
                  <Button
                    disabled={
                      selectedItems.length === 0 || pendingAction !== null
                    }
                    loading={pendingAction === "REJECT"}
                    onClick={() => setShowRejectModal(true)}
                  >
                    Reject
                  </Button>
                </SpaceBetween>
              ) : undefined
            }
          >
            Files
          </Header>
        }
        items={kbApprovalDocuments}
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) =>
          setSelectedItems(detail.selectedItems)
        }
        selectionType={isPending && isApprover ? "multi" : undefined}
        trackBy="kbApprovalDocumentId"
        empty={
          <Box textAlign="center" color="inherit">
            <b>No documents</b>
          </Box>
        }
        columnDefinitions={[
          {
            id: "fileName",
            header: "File name",
            width: isDeletionReview ? "30%" : "25%",
            cell: (item: KBApprovalDocument) => {
              const isAtlasPreview = isDeletionReview && !!item.atlasObjectKey;
              const isExistingDocPreview =
                !isAtlasPreview && item.documentSource === "GAIA_OBJECTS";
              const previewId = isAtlasPreview
                ? item.atlasObjectKey
                : item.documentId;
              return previewId ? (
                <Link
                  to="#"
                  className={styles.fileName}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedDocumentId(previewId);
                    setSource(
                      isAtlasPreview
                        ? "atlas"
                        : isExistingDocPreview
                          ? "documents"
                          : "documents-readonly",
                    );
                    setSplitPanelOpen(true);
                  }}
                >
                  {getFileNameWithExtension(item)}
                </Link>
              ) : (
                <span>{getFileNameWithExtension(item)}</span>
              );
            },
          },
          ...(!isDeletionReview
            ? [
                {
                  id: "type",
                  header: "Type",
                  width: "12%",
                  cell: (item: KBApprovalDocument) =>
                    item.replaceExisting ? (
                      <StatusIndicator type="info">
                        Replaces existing
                      </StatusIndicator>
                    ) : (
                      "New"
                    ),
                },
                {
                  id: "size",
                  header: "Size",
                  width: "10%",
                  cell: (item: KBApprovalDocument) => item.size || "-",
                },
              ]
            : []),
          {
            id: "approvalStatus",
            header: "Approval status",
            width: isDeletionReview ? "20%" : "18%",
            cell: (item: KBApprovalDocument) => {
              const mapped = approvalStatusMap[item.approvalStatus ?? ""];
              if (!mapped)
                return (
                  <StatusIndicator type="pending">
                    {item.approvalStatus}
                  </StatusIndicator>
                );
              return (
                <StatusIndicator type={mapped.type}>
                  {mapped.label}
                </StatusIndicator>
              );
            },
          },
          {
            id: "reviewedBy",
            header: "Reviewed by",
            width: isDeletionReview ? "20%" : "15%",
            cell: (item: KBApprovalDocument) =>
              item.reviewedBy?.join(", ") || "-",
          },
          {
            id: "rejectionReason",
            header: "Rejection reason",
            width: isDeletionReview ? "30%" : "20%",
            cell: (item: KBApprovalDocument) => (
              <span className={kbStyles.rejectionReason}>
                {item.rejectionReason || "-"}
              </span>
            ),
          },
        ]}
      />

      <Modal
        visible={showRejectModal}
        onDismiss={() => {
          if (pendingAction === "REJECT") return;
          setShowRejectModal(false);
          setRejectReason("");
        }}
        header="Reject Selected Documents"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                disabled={pendingAction === "REJECT"}
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!rejectReason.trim() || pendingAction === "REJECT"}
                loading={pendingAction === "REJECT"}
                onClick={handleBulkRejectConfirm}
              >
                Reject
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField label="Provide a reason" description="Required">
          <Textarea
            value={rejectReason}
            onChange={({ detail }) => setRejectReason(detail.value)}
            placeholder="Enter reason for rejection"
          />
        </FormField>
      </Modal>
    </>
  );
};

export default KBApprovalDocumentsTable;
